import { CommonModule } from '@angular/common';
import {
    Component,
    DestroyRef,
    inject,
    OnInit
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TooltipModule } from 'primeng/tooltip';

import { AuthService } from '@/auth/services';
import {
    valueInputTypeForOperator,
    WebVisibilityRule,
    WebVisibilityValueInputType
} from '@/interfaces/web-visibility-rule';
import { RatesVisibilitySyncStatus } from '@/interfaces/rates-visibility-sync-status';
import { ConditionValueInputComponent } from '@/pages/app-settings/components/condition-value-input.component';
import { WebVisibilityRulesService } from '@/service/web-visibility-rules.service';
import { timer } from 'rxjs';
import { switchMap, tap } from 'rxjs/operators';

@Component({
    selector: 'app-web-visibility-rules',
    standalone: true,
    templateUrl: './web-visibility-rules.component.html',
    imports: [
        CommonModule,
        FormsModule,
        ButtonModule,
        CheckboxModule,
        InputTextModule,
        SelectModule,
        TableModule,
        TooltipModule,
        ConditionValueInputComponent,
    ],
})
export class WebVisibilityRulesComponent implements OnInit {
    loading = false;
    saving = false;
    rules: WebVisibilityRule[] = [];
    operatorOptions: { label: string; value: string }[] = [];
    valueInputTypeOptions: { label: string; value: WebVisibilityValueInputType }[] = [];
    activeSyncStatus: RatesVisibilitySyncStatus | null = null;

    private readonly destroyRef = inject(DestroyRef);

    constructor(
        public authService: AuthService,
        private service: WebVisibilityRulesService,
        private messageService: MessageService,
    ) {
    }

    get canEdit(): boolean {
        return this.authService.hasPermission('settings.web_visibility_rules.update');
    }

    get syncInProgress(): boolean {
        return !!this.activeSyncStatus?.is_active;
    }

    ngOnInit(): void {
        this.load();
        this.startSyncStatusPolling();
    }

    load(): void {
        this.loading = true;

        this.service.editorOptions().subscribe({
            next: (options) => {
                this.operatorOptions = options.operators ?? [];
                this.valueInputTypeOptions = options.value_input_types ?? [];
                this.service.list().subscribe({
                    next: (rules) => {
                        this.rules = rules.map((rule) => this.normalizeRule(rule));
                        this.loading = false;
                    },
                    error: () => {
                        this.loading = false;
                        this.toastError('Failed to load web visibility rules.');
                    }
                });
            },
            error: () => {
                this.loading = false;
                this.toastError('Failed to load editor options.');
            }
        });
    }

    onOperatorChange(rule: WebVisibilityRule, operator: string | null): void {
        const nextType = valueInputTypeForOperator(operator);
        if (rule.value_input_type === nextType) {
            return;
        }

        rule.value_input_type = nextType;
        this.onValueInputTypeChange(rule);
    }

    onValueInputTypeChange(rule: WebVisibilityRule): void {
        if (rule.value_input_type === 'checkbox') {
            rule.values = ['true'];
            return;
        }

        rule.values = [];
    }

    addRule(): void {
        const operator = this.operatorOptions[0]?.value ?? null;

        this.rules = [
            ...this.rules,
            {
                label: '',
                field_key: '',
                rule_kind: 'rate_field',
                operator,
                value_input_type: valueInputTypeForOperator(operator),
                values: [],
                is_active: true,
            },
        ];
    }

    removeRule(index: number): void {
        this.rules = this.rules.filter((_, i) => i !== index);
    }

    save(): void {
        if (!this.canEdit || this.syncInProgress) {
            return;
        }

        this.saving = true;
        this.service.bulkSave(this.rules).subscribe({
            next: (saved) => {
                this.rules = saved.map((rule) => this.normalizeRule(rule));
                this.saving = false;
                this.refreshSyncStatus();
                this.messageService.add({
                    severity: 'success',
                    detail: 'Web visibility rules saved. Rates visibility is being recalculated in the background.',
                });
            },
            error: (err) => {
                this.saving = false;
                this.toastError(err.error?.message ?? err.error?.error ?? 'Failed to save rules.');
            }
        });
    }

    private startSyncStatusPolling(): void {
        timer(0, 5000).pipe(
            takeUntilDestroyed(this.destroyRef),
            switchMap(() => this.service.syncStatus()),
            tap((status) => {
                this.activeSyncStatus = status;
            }),
        ).subscribe();
    }

    private refreshSyncStatus(): void {
        this.service.syncStatus().subscribe({
            next: (status) => {
                this.activeSyncStatus = status;
            },
        });
    }

    private normalizeRule(rule: WebVisibilityRule): WebVisibilityRule {
        return {
            ...rule,
            values: rule.values ?? [],
            value_input_type: rule.value_input_type ?? 'text',
        };
    }

    private toastError(detail: string): void {
        this.messageService.add({ severity: 'error', detail });
    }
}
